import { graphlib } from "@dagrejs/dagre";
import { processTemplate, setAtPath } from "@imminently/decisively-core";
import { isClearMarker } from "./ContactUpdate";
import { omit } from "lodash";
import moment from "moment";
import { JS_BEGINNING_OF_TIME_ISO, JS_END_OF_TIME_ISO } from "./Constants";
import { csvDateFormatIncludesTime, parseCsvDateWithFormats } from "./CsvDateFormats";
import { normalizeMappedEmail } from "./EmailMapping";
import { getPathToEntity, mapRequestData, parseCSV } from "./RequestMapping";

export const CONTACT_ROOT_FIELDS = ["firstName", "lastName", "email", "phoneNumber"] as const;
export const RESERVED_CONTACT_FIELDS = ["id", "firstName", "lastName", "name", "email"] as const;
export const CONTACT_EXTERNAL_IDENTIFIERS_FIELD = "__contactIdentifiers";
const UNSAFE_PATH_SEGMENTS = new Set(["__proto__", "prototype", "constructor"]);

type ContactRecord = Record<string, any>;
type ContactCustomData = Record<string, unknown>;
export interface ContactExternalIdentifier {
  namespace: string;
  key: string;
  value: string;
}

const DEFAULT_EXTERNAL_IDENTIFIER_KEY = "id";
const DEFAULT_EXTERNAL_IDENTIFIER_NAMESPACE = "system";
const FALLBACK_ENTITY_IDENTIFIER_PREFIX = "id-";
const ENTITY_IDENTIFIER_VALUES_REQUIRING_PREFIX = new Set(["0", "false", "null", "undefined", "unknown"]);

type StructuredCsvMapOptions = {
  reservedFields: readonly string[];
  mergeRowsByReservedId: boolean;
  typeLabel: "contact" | "entity";
};

const CONTACT_CSV_MAP_OPTIONS: StructuredCsvMapOptions = {
  reservedFields: RESERVED_CONTACT_FIELDS,
  mergeRowsByReservedId: true,
  typeLabel: "contact",
};

const LEGACY_ENTITY_CSV_MAP_OPTIONS: StructuredCsvMapOptions = {
  reservedFields: [],
  mergeRowsByReservedId: false,
  typeLabel: "entity",
};

const encodeBase64 = (value: string): string => {
  if (typeof (globalThis as any).window === "undefined" && typeof Buffer !== "undefined") {
    return Buffer.from(value, "utf8").toString("base64");
  }
  return btoa(value);
};

const isLikelyBase64 = (value: string): boolean => {
  const trimmed = String(value || "").trim();
  if (!trimmed || trimmed.length % 4 !== 0) return false;
  return /^[A-Za-z0-9+/]+={0,2}$/.test(trimmed);
};

const assertSafePathSegment = (segment: string, context: string) => {
  if (UNSAFE_PATH_SEGMENTS.has(segment)) {
    throw new Error(`Unsafe path segment '${segment}' in ${context}`);
  }
};

const assertSafeExtensionTemplate = (value: unknown): void => {
  if (Array.isArray(value)) {
    for (const child of value) assertSafeExtensionTemplate(child);
    return;
  }
  if (!value || typeof value !== "object") return;

  for (const [key, child] of Object.entries(value)) {
    assertSafePathSegment(key, "Extension JSON template");
    assertSafeExtensionTemplate(child);
  }
};

const EXTENSION_TYPED_VALUE_PATTERN = /^\{\{\s*([^}|]+?)\s*\|\s*(number|string|boolean|json)\s*\}\}$/i;
const EXTENSION_DATE_PATTERN = /\{\{\s*([^}|]+?)\s*\|\s*date(?:\s+([^}]+?))?\s*\}\}/gi;
const EXTENSION_LOWERCASE_PATTERN = /\{\{\s*([^}|]+?)\s*\|\s*lowercase\s*\}\}/gi;

const renderExtensionDates = (template: string, row: Record<string, any>): string =>
  template.replace(EXTENSION_DATE_PATTERN, (_match, columnName, sourceFormat) => {
    const column = String(columnName).trim();
    const rawValue = processTemplate(`{{${column}}}`, row);
    const normalizedFormat = typeof sourceFormat === "string" ? sourceFormat.trim() : "";
    const { date: parsed } = normalizedFormat
      ? parseCsvDateWithFormats(String(rawValue), normalizedFormat)
      : { date: moment.utc(String(rawValue), moment.ISO_8601, true) };
    if (!parsed.isValid()) {
      throw new Error(
        normalizedFormat
          ? `Unable to parse '${String(rawValue)}' for template column '${column}' with date format '${normalizedFormat}'`
          : `Unable to parse '${String(rawValue)}' for template column '${column}' as a date`,
      );
    }
    return parsed.format("YYYY-MM-DD");
  });

const renderExtensionLowercase = (template: string, row: Record<string, any>): string =>
  template.replace(EXTENSION_LOWERCASE_PATTERN, (_match, columnName) => {
    const column = String(columnName).trim();
    const rawValue = processTemplate(`{{${column}}}`, row);
    return rawValue === undefined || rawValue === null ? "" : String(rawValue).toLowerCase();
  });

const renderTypedExtensionValue = (template: string, row: Record<string, any>): unknown => {
  const match = template.match(EXTENSION_TYPED_VALUE_PATTERN);
  if (!match) {
    return processTemplate(renderExtensionLowercase(renderExtensionDates(template, row), row), row);
  }

  const rawValue = processTemplate(`{{${match[1].trim()}}}`, row);
  const valueType = match[2].toLowerCase();
  if (valueType === "string") return rawValue === undefined || rawValue === null ? "" : String(rawValue);
  if (valueType === "number") {
    const numberValue = Number(rawValue);
    if (!Number.isFinite(numberValue)) {
      throw new Error(`Unable to convert '${String(rawValue)}' to a number for template column '${match[1].trim()}'`);
    }
    return numberValue;
  }
  if (valueType === "boolean") {
    if (typeof rawValue === "boolean") return rawValue;
    const normalized = String(rawValue).trim().toLowerCase();
    if (["true", "1", "yes"].includes(normalized)) return true;
    if (["false", "0", "no"].includes(normalized)) return false;
    throw new Error(`Unable to convert '${String(rawValue)}' to a boolean for template column '${match[1].trim()}'`);
  }

  try {
    return JSON.parse(String(rawValue));
  } catch {
    throw new Error(`Unable to parse JSON for template column '${match[1].trim()}'`);
  }
};

const renderExtensionTemplate = (value: unknown, row: Record<string, any>): unknown => {
  if (typeof value === "string") return renderTypedExtensionValue(value, row);
  if (Array.isArray(value)) return value.map((child) => renderExtensionTemplate(child, row));
  if (!value || typeof value !== "object") return value;

  return Object.fromEntries(
    Object.entries(value).map(([key, child]) => [key, renderExtensionTemplate(child, row)]),
  );
};

const getExtensionJsonTemplate = (value: unknown): Record<string, unknown> | undefined => {
  if (typeof value === "string") {
    try {
      const parsed: unknown = JSON.parse(value);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error("must be a JSON object");
      }
      return parsed as Record<string, unknown>;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "must be valid JSON";
      throw new Error(`Invalid Extension JSON template: ${message}`);
    }
  }
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return undefined;
};

const normalizeHeaderKey = (value: string) => value.trim().replace(/\s+/g, " ").toLowerCase();

type EnumMapConfig = {
  exact: Map<string, string>;
  lower: Map<string, string>;
};

const resolveRowHeaderKey = (header: string, row: Record<string, any>) => {
  if (Object.prototype.hasOwnProperty.call(row, header)) {
    return header;
  }

  const normalizedHeader = normalizeHeaderKey(header);
  if (!normalizedHeader) return undefined;

  return Object.keys(row).find((key) => normalizeHeaderKey(key) === normalizedHeader);
};

const parseQuotedLiteral = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (trimmed.length < 2) return undefined;
  const quote = trimmed[0];
  if ((quote !== "\"" && quote !== "'") || trimmed[trimmed.length - 1] !== quote) {
    return undefined;
  }
  const inner = trimmed.slice(1, -1);
  if (quote === "\"") {
    return inner.replace(/\\"/g, "\"");
  }
  return inner.replace(/\\'/g, "'");
};

const enumMapConfigCache = new WeakMap<object, EnumMapConfig | null>();

const parseEnumMapInput = (input: unknown, columnLabel: string): Record<string, unknown> | undefined => {
  if (input === undefined || input === null) return undefined;
  if (typeof input === "string") {
    const trimmed = input.trim();
    if (!trimmed) return undefined;
    try {
      const parsed = JSON.parse(trimmed);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error("enumMap must be a JSON object");
      }
      return parsed as Record<string, unknown>;
    } catch (error: any) {
      throw new Error(
        `Invalid enum map for column '${columnLabel}': ${error?.message || "must be valid JSON object"}`,
      );
    }
  }
  if (typeof input === "object" && !Array.isArray(input)) {
    return input as Record<string, unknown>;
  }
  throw new Error(`Invalid enum map for column '${columnLabel}': must be object or JSON string`);
};

const getEnumMapConfig = (column: any): EnumMapConfig | undefined => {
  if (!column || typeof column !== "object") return undefined;

  if (enumMapConfigCache.has(column)) {
    return enumMapConfigCache.get(column) || undefined;
  }

  const columnLabel = getNonEmptyString(column?.header) || getNonEmptyString(column?.field) || "unknown";
  const enumMapInput = parseEnumMapInput(column?.enumMap, columnLabel);
  if (!enumMapInput) {
    enumMapConfigCache.set(column, null);
    return undefined;
  }

  const exact = new Map<string, string>();
  const lower = new Map<string, string>();
  Object.entries(enumMapInput).forEach(([rawKey, rawValue]) => {
    const key = String(rawKey).trim();
    if (!key) return;
    const mappedValue = rawValue === undefined || rawValue === null ? "" : String(rawValue);
    exact.set(key, mappedValue);
    lower.set(key.toLowerCase(), mappedValue);
  });

  const compiled: EnumMapConfig = { exact, lower };
  enumMapConfigCache.set(column, compiled);
  return compiled;
};

const applyEnumMapping = (value: unknown, column: any): unknown => {
  const columnType = String(column?.type || column?.columnType || "").trim().toLowerCase();
  const enumEnabled = columnType === "enum" || column?.enumMap !== undefined;
  if (!enumEnabled) return value;

  const enumMap = getEnumMapConfig(column);
  const enumDefault = getNonEmptyString(column?.enumDefault);
  if (!enumMap && enumDefault === undefined) return value;
  if (value === undefined || value === null) return value;

  const source = String(value).trim();
  if (!source) return enumDefault !== undefined ? enumDefault : value;
  if (enumMap?.exact.has(source)) {
    return enumMap.exact.get(source);
  }

  const lowerMatch = enumMap?.lower.get(source.toLowerCase());
  if (lowerMatch !== undefined) {
    return lowerMatch;
  }

  if (enumDefault !== undefined) {
    return enumDefault;
  }
  return value;
};

const isDateColumn = (column: any) => (
  String(column?.type || column?.columnType || "").trim().toLowerCase() === "date"
  || column?.asDate === true
  || column?.asDate === "true"
  || String(column?.valueType || "").trim().toLowerCase() === "date"
);

const isTimeColumn = (column: any) => (
  String(column?.type || column?.columnType || "").trim().toLowerCase() === "time"
  || String(column?.valueType || "").trim().toLowerCase() === "time"
);

const toIsoTimestamp = (value: unknown, column: any, mappingDef?: any): unknown => {
  if (!isDateColumn(column)) return value;
  if (value === undefined || value === null) return value;
  const source = String(value).trim();
  if (!source) return value;

  const mappedBoundary = resolveCsvDateBoundaryValue(source, mappingDef);
  if (mappedBoundary) return mappedBoundary;

  const sourceFormat = getNonEmptyString(column?.dateFormat);
  const { date: parsed, matchedFormat } = sourceFormat
    ? parseCsvDateWithFormats(source, sourceFormat)
    : { date: moment.utc(source, moment.ISO_8601, true), matchedFormat: undefined };

  if (!parsed.isValid()) {
    const columnLabel = getNonEmptyString(column?.header) || getNonEmptyString(column?.field) || "unknown";
    throw new Error(
      sourceFormat
        ? `Unable to parse date '${source}' for column '${columnLabel}' with format '${sourceFormat}'`
        : `Unable to parse date '${source}' for column '${columnLabel}'`,
    );
  }

  if (matchedFormat && !csvDateFormatIncludesTime(matchedFormat)) return parsed.format("YYYY-MM-DD");

  return parsed.format("YYYY-MM-DD[T]HH:mm:ss.000[Z]");
};

const toWallClockTime = (value: unknown, column: any): unknown => {
  if (!isTimeColumn(column)) return value;
  if (value === undefined || value === null) return value;
  const source = String(value).trim();
  if (!source) return value;

  const sourceFormat = getNonEmptyString(column?.dateFormat) || getNonEmptyString(column?.timeFormat);
  const parsed = sourceFormat
    ? moment.utc(source, sourceFormat, true)
    : moment.utc(source, ["HH:mm:ss", "HHmmss", "Hmmss", "HH:mm", "Hmm"], true);

  if (!parsed.isValid()) {
    const columnLabel = getNonEmptyString(column?.header) || getNonEmptyString(column?.field) || "unknown";
    throw new Error(
      sourceFormat
        ? `Unable to parse time '${source}' for column '${columnLabel}' with format '${sourceFormat}'`
        : `Unable to parse time '${source}' for column '${columnLabel}'`,
    );
  }

  return parsed.format("HH:mm:ss");
};

const finalizeColumnValue = (value: unknown, column: any, mappingDef?: any): unknown => {
  const unquoted = typeof value === "string" ? (parseQuotedLiteral(value) ?? value) : value;
  const mapped = applyEnumMapping(unquoted, column);
  const normalizedEmail = normalizeMappedEmail(mapped, column);
  return toWallClockTime(toIsoTimestamp(normalizedEmail, column, mappingDef), column);
};

const DATE_FORMAT_LITERAL_REGEX = /\[[^\]]*\]/g;
const DATE_FORMAT_TIME_TOKEN_REGEX = /[HhmsSAaZz]/;


const finalizeEntityColumnValue = (value: unknown, column: any, mappingDef?: any): unknown => {
  const unquoted = typeof value === "string" ? (parseQuotedLiteral(value) ?? value) : value;
  const mapped = applyEnumMapping(unquoted, column);
  if (!isDateColumn(column)) return toWallClockTime(mapped, column);
  if (mapped === undefined || mapped === null) return mapped;
  const source = String(mapped).trim();
  if (!source) return mapped;
  const mappedBoundary = resolveCsvDateBoundaryValue(source, mappingDef);
  if (mappedBoundary) return mappedBoundary;
  const sourceFormat = getNonEmptyString(column?.dateFormat);
  const { date: parsed, matchedFormat } = sourceFormat
    ? parseCsvDateWithFormats(source, sourceFormat)
    : { date: moment.utc(source, moment.ISO_8601, true), matchedFormat: undefined };
  if (!parsed.isValid()) {
    const columnLabel = getNonEmptyString(column?.header) || getNonEmptyString(column?.field) || "unknown";
    throw new Error(
      sourceFormat
        ? `Unable to parse date '${source}' for column '${columnLabel}' with format '${sourceFormat}'`
        : `Unable to parse date '${source}' for column '${columnLabel}'`,
    );
  }
  const dateValue = matchedFormat && !csvDateFormatIncludesTime(matchedFormat)
    ? parsed.format("YYYY-MM-DD")
    : parsed.format("YYYY-MM-DD[T]HH:mm:ss.000[Z]");
  return toWallClockTime(dateValue, column);
};

const getContactColumnValue = (column: any, row: Record<string, any>, mappingDef?: any) => {
  if (
    column?.value !== undefined &&
    !(typeof column.value === "string" && column.value.length === 0)
  ) {
    const value = typeof column.value === "string" && column.value.includes("{{")
      ? processTemplate(column.value, row)
      : column.value;
    return finalizeColumnValue(value, column, mappingDef);
  }

  const header = typeof column?.header === "string" ? column.header : "";
  if (!header) return undefined;

  if (header.includes("{{")) {
    const value = processTemplate(header, row);
    return finalizeColumnValue(value, column, mappingDef);
  }

  const quotedLiteral = parseQuotedLiteral(header);
  if (quotedLiteral !== undefined) {
    return finalizeColumnValue(quotedLiteral, column, mappingDef);
  }

  const resolvedHeaderKey = resolveRowHeaderKey(header, row);
  if (resolvedHeaderKey) {
    return finalizeColumnValue(row[resolvedHeaderKey], column, mappingDef);
  }

  return undefined;
};

const getEntityColumnValue = (column: any, row: Record<string, any>, mappingDef?: any) => {
  if (column?.value !== undefined && !(typeof column.value === "string" && column.value.length === 0)) {
    const value = typeof column.value === "string" && column.value.includes("{{")
      ? processTemplate(column.value, row)
      : column.value;
    return finalizeEntityColumnValue(value, column, mappingDef);
  }
  const header = typeof column?.header === "string" ? column.header : "";
  if (!header) return undefined;
  if (header.includes("{{")) return finalizeEntityColumnValue(processTemplate(header, row), column, mappingDef);
  const quotedLiteral = parseQuotedLiteral(header);
  if (quotedLiteral !== undefined) return finalizeEntityColumnValue(quotedLiteral, column, mappingDef);
  const resolvedHeaderKey = resolveRowHeaderKey(header, row);
  return resolvedHeaderKey ? finalizeEntityColumnValue(row[resolvedHeaderKey], column, mappingDef) : undefined;
};

const toCsvRows = async (input: unknown): Promise<Record<string, any>[]> => {
  if (Array.isArray(input)) {
    return input.filter((item) => item && typeof item === "object") as Record<string, any>[];
  }

  if (input && typeof input === "object") {
    const dataRows = (input as Record<string, unknown>).data;
    if (Array.isArray(dataRows)) {
      return dataRows.filter((item) => item && typeof item === "object") as Record<string, any>[];
    }
  }

  if (typeof input === "string") {
    const trimmed = input.trim();
    if (isLikelyBase64(trimmed)) {
      try {
        return await parseCSV(trimmed);
      } catch {
        // Fall through to treat the value as raw CSV text.
      }
    }
    return parseCSV(encodeBase64(input));
  }

  throw new Error("CSV contact/entity mapping requires CSV text, base64 CSV, or parsed rows");
};

/**
 * The relationship whose instance is the contact itself (marked `contactEntity`, as the contacts service finds it). Its
 * attributes belong on the contact, not in a nested `<entity>/1` record.
 */
const getContactEntityName = (relationships: any[] = []): string | undefined =>
  relationships.find((relationship) => Boolean(relationship?.contactEntity) && relationship?.name !== "global")?.name;

/** Drops the contact entity from the front of an entity path: its instance is the contact. */
const withoutContactEntity = (path: string[], relationships: any[]): string[] => {
  const contactEntity = getContactEntityName(relationships);
  return contactEntity && path[0] === contactEntity ? path.slice(1) : path;
};

/**
 * Where an entity's records go within a contact, as path segments: the contact entity's own attributes go on the
 * contact (no segments), and its children under the contact, e.g. `assignments` rather than `employees/assignments`.
 */
export const getContactEntityPath = (entity: string, relationships: any[] = []): string[] => {
  const path = getPathToEntity(entity, relationships).filter((segment) => Boolean(segment) && segment !== "global");
  return withoutContactEntity(path.length > 0 ? path : [entity], relationships);
};

const getEntityContainmentPath = (
  entity: string,
  relationships: any[],
  cache: Map<string, string[]>,
): string[] => {
  if (cache.has(entity)) {
    return cache.get(entity) || [];
  }

  const resolvedPath = getContactEntityPath(entity, relationships);
  cache.set(entity, resolvedPath);
  return resolvedPath;
};

/**
 * An explicit column path without the contact entity at its front (e.g. `employees`, which mapping tools saved as the
 * default for the contact entity's attributes), with its record IDs keyed to match.
 */
const withoutContactEntityInExplicitPath = (
  pathSegments: string[],
  explicitIdsByPath: Map<string, string>,
  relationships: any[],
): { entityPath: string[]; explicitIdsByPath: Map<string, string> } => {
  const contactEntity = getContactEntityName(relationships);
  if (!contactEntity || pathSegments[0] !== contactEntity) {
    return { entityPath: pathSegments, explicitIdsByPath };
  }
  const idsByPath = new Map<string, string>();
  explicitIdsByPath.forEach((id, key) => {
    if (key.startsWith(`${contactEntity}.`)) idsByPath.set(key.slice(contactEntity.length + 1), id);
  });
  return { entityPath: pathSegments.slice(1), explicitIdsByPath: idsByPath };
};

const normalizePathKey = (entityPath: string[]) => entityPath.join(".");

const normalizeIdentifierValue = (value: unknown): string => {
  const stringValue = value === null ? "null" : value === undefined ? "undefined" : String(value).trim();
  const normalizedValue = stringValue || "empty";
  // Decisively cannot resolve falsy entity IDs, so retain the source identity behind a safe, stable prefix.
  const identifierValue =
    ENTITY_IDENTIFIER_VALUES_REQUIRING_PREFIX.has(normalizedValue.toLowerCase()) || normalizedValue === "empty"
      ? `${FALLBACK_ENTITY_IDENTIFIER_PREFIX}${normalizedValue.toLowerCase()}`
      : normalizedValue;
  return identifierValue.replace(/\//g, "-");
};

const normalizeEntityIdentifierValue = (value: unknown): string | undefined => {
  if (value === undefined || value === null) return undefined;
  const trimmed = String(value).trim();
  if (!trimmed) return undefined;
  return trimmed.replace(/\//g, "-");
};

const normalizeContactRecordId = (value: unknown): string | undefined => {
  if (value === undefined || value === null) return undefined;
  const trimmed = String(value).trim();
  if (!trimmed) return undefined;
  return trimmed;
};

const getNonEmptyString = (value: unknown): string | undefined => {
  if (value === undefined || value === null) return undefined;
  const trimmed = String(value).trim();
  return trimmed ? trimmed : undefined;
};

const isExternalIdentifierColumn = (column: any): boolean => (
  column?.externalIdentifier === true
  || column?.externalIdentifier === "true"
);

const getExternalIdentifierConfig = (column: any): { namespace: string; key: string } | undefined => {
  if (!isExternalIdentifierColumn(column)) return undefined;
  const namespace = getNonEmptyString(column?.externalIdentifierNamespace);
  const key = getNonEmptyString(column?.externalIdentifierKey) || DEFAULT_EXTERNAL_IDENTIFIER_KEY;
  if (!namespace) return undefined;
  return { namespace, key };
};

const getMappingExternalIdentifierConfig = (
  mappingDef: any,
): { namespace: string; key: string; column: string } | undefined => {
  const config = mappingDef?.externalIdentifier;
  if (!config || typeof config !== "object" || config.enabled === false) return undefined;

  const namespace =
    getNonEmptyString(config.source)
    || getNonEmptyString(config.namespace)
    || getNonEmptyString(config.externalIdentifierNamespace)
    || DEFAULT_EXTERNAL_IDENTIFIER_NAMESPACE;
  const key =
    getNonEmptyString(config.type)
    || getNonEmptyString(config.key)
    || getNonEmptyString(config.externalIdentifierKey)
    || DEFAULT_EXTERNAL_IDENTIFIER_KEY;
  const column =
    getNonEmptyString(config.column)
    || getNonEmptyString(config.header)
    || getNonEmptyString(config.contactColumn);

  if (!namespace || !column) return undefined;
  return { namespace, key, column };
};

const addExternalIdentifierValue = (
  record: ContactRecord,
  config: { namespace: string; key: string },
  value: unknown,
) => {
  const normalizedValue = normalizeContactRecordId(value);
  if (!normalizedValue) return;

  const existing = Array.isArray(record[CONTACT_EXTERNAL_IDENTIFIERS_FIELD])
    ? record[CONTACT_EXTERNAL_IDENTIFIERS_FIELD] as ContactExternalIdentifier[]
    : [];
  if (existing.some((identifier) =>
    identifier.namespace === config.namespace
    && identifier.key === config.key
    && identifier.value === normalizedValue
  )) {
    return;
  }

  record[CONTACT_EXTERNAL_IDENTIFIERS_FIELD] = [
    ...existing,
    {
      namespace: config.namespace,
      key: config.key,
      value: normalizedValue,
    },
  ];
};

const addExternalIdentifier = (
  record: ContactRecord,
  column: any,
  value: unknown,
) => {
  const config = getExternalIdentifierConfig(column);
  if (!config) return;
  addExternalIdentifierValue(record, config, value);
};

const addMappingExternalIdentifier = (
  record: ContactRecord,
  mappingDef: any,
  row: Record<string, any>,
) => {
  const config = getMappingExternalIdentifierConfig(mappingDef);
  if (!config) return;
  const value = getContactColumnValue({ header: config.column }, row, mappingDef);
  addExternalIdentifierValue(record, config, value);
};

const resolveCsvDateBoundaryValue = (source: string, mappingDef: any): string | undefined => {
  const beginningToken = getNonEmptyString(mappingDef?.csvBeginningOfTimeValue);
  if (beginningToken && source === beginningToken) {
    return JS_BEGINNING_OF_TIME_ISO;
  }

  const endToken = getNonEmptyString(mappingDef?.csvEndOfTimeValue);
  if (endToken && source === endToken) {
    return JS_END_OF_TIME_ISO;
  }

  return undefined;
};

const getSetAtPathForField = (
  entityPath: string[],
  field: string,
  identifiersByPath: Map<string, string>,
) => {
  assertSafePathSegment(field, "field");
  const segments: string[] = [];
  entityPath.forEach((segment, index) => {
    assertSafePathSegment(segment, "entity path");
    const pathKey = normalizePathKey(entityPath.slice(0, index + 1));
    // Contact CSV mapping creates a single instance per entity path per line
    // unless a path-level identifier column provides a concrete @id.
    const id = identifiersByPath.get(pathKey) || "1";
    segments.push(segment, id);
  });
  segments.push(field);
  return segments.join("/");
};

const expandShorthandSetPathSegments = (segments: string[]): string[] => {
  if (segments.length <= 1) return segments;
  const expanded: string[] = [];
  segments.forEach((segment, index) => {
    const isLast = index === segments.length - 1;
    if (!isLast) {
      expanded.push(segment, "1");
      return;
    }
    if (segments.length === 2) {
      expanded.push(segment, "1");
      return;
    }
    expanded.push(segment);
  });
  return expanded;
};

const normalizeEntitySetPath = (field: string): string => {
  const trimmed = String(field || "").trim();
  if (!trimmed) return "";
  const normalized = trimmed.replace(/\[(\d+)\]/g, "/$1");
  const delimiter = normalized.includes("/") ? "/" : ".";
  const rawSegments = normalized
    .split(delimiter)
    .map((segment) => segment.trim())
    .filter(Boolean);
  const finalSegments = delimiter === "/"
    ? rawSegments
    : expandShorthandSetPathSegments(rawSegments);
  finalSegments.forEach((segment) => assertSafePathSegment(segment, "field"));
  return finalSegments.join("/");
};

const setEntityFieldValueAtPath = (record: ContactRecord, field: string, value: any) => {
  const normalizedPath = normalizeEntitySetPath(field);
  if (!normalizedPath) return;
  const segments = normalizedPath.split("/").filter(Boolean);
  const isAttributePath = segments.length % 2 === 1;
  if (isAttributePath) {
    setAtPath(record, normalizedPath, value);
    return;
  }
  if (value && typeof value === "object" && !Array.isArray(value)) {
    setAtPath(record, normalizedPath, value);
  } else if (value !== undefined) {
    setAtPath(record, normalizedPath, { value });
  } else {
    setAtPath(record, normalizedPath, {});
  }
};

const parseEntityPathOverride = (path: unknown): { pathSegments: string[]; isExplicit: boolean } => {
  if (typeof path !== "string") {
    return { pathSegments: [], isExplicit: false };
  }
  return {
    pathSegments: path
      .trim()
      .replace(/\[(\d+)\]/g, "")
      .split(/[./]/)
      .map((segment) => {
        const trimmed = segment.trim();
        if (trimmed) {
          assertSafePathSegment(trimmed, "path");
        }
        return trimmed;
      })
      .filter((segment) => Boolean(segment) && segment !== "global"),
    isExplicit: true,
  };
};

const parseTemplatedEntityPathOverride = (
  path: unknown,
  row: Record<string, any>,
): { pathSegments: string[]; explicitIdsByPath: Map<string, string>; isExplicit: boolean } => {
  if (typeof path !== "string") {
    return { pathSegments: [], explicitIdsByPath: new Map<string, string>(), isExplicit: false };
  }

  const trimmedPath = path.trim();
  if (!trimmedPath) {
    return { pathSegments: [], explicitIdsByPath: new Map<string, string>(), isExplicit: true };
  }

  const rawTokens = trimmedPath
    .replace(/\[(\d+)\]/g, "/$1")
    .split("/")
    .flatMap((part) => part.split("."))
    .map((token) => token.trim())
    .filter(Boolean);

  const pathSegments: string[] = [];
  const explicitIdsByPath = new Map<string, string>();

  rawTokens.forEach((rawToken) => {
    const isPureTemplateToken = /^{{[\s\S]+}}$/.test(rawToken);
    const resolvedValue = rawToken.includes("{{") ? processTemplate(rawToken, row) : rawToken;

    if (isPureTemplateToken) {
      if (pathSegments.length === 0) return;
      const normalizedId = normalizeIdentifierValue(resolvedValue);
      assertSafePathSegment(normalizedId, "path identifier");
      const parentPathKey = normalizePathKey(pathSegments);
      explicitIdsByPath.set(parentPathKey, normalizedId);
      return;
    }

    const resolvedToken = String(resolvedValue ?? "").trim();
    if (!resolvedToken) return;
    if (resolvedToken === "global") return;
    assertSafePathSegment(resolvedToken, "path");
    pathSegments.push(resolvedToken);
  });

  return { pathSegments, explicitIdsByPath, isExplicit: true };
};

const isIdentifierColumn = (column: any) => (
  column?.identifier === true
  || column?.identifier === "true"
  || String(column?.type || column?.columnType || "").trim().toLowerCase() === "identifier"
);

const getContactMultilineEntityValue = (entityMapping: any, entityToken: string): { entity?: string; path?: string } => {
  const mappingValue = entityMapping?.mapping?.[entityToken];
  if (mappingValue && typeof mappingValue === "object") {
    return {
      entity: getNonEmptyString((mappingValue as any).entity) || getNonEmptyString((mappingValue as any).value),
      path: getNonEmptyString((mappingValue as any).path),
    };
  }

  return {
    entity: getNonEmptyString(mappingValue),
    path: getNonEmptyString(entityMapping?.pathMapping?.[entityToken]),
  };
};

const resolveMultilineEntityPath = (
  entityToken: string,
  entityMapping: any,
  relationships: any[],
  entityPathCache: Map<string, string[]>,
): string[] => {
  const mapping = getContactMultilineEntityValue(entityMapping, entityToken);
  const explicitPath = parseEntityPathOverride(mapping.path);
  if (explicitPath.pathSegments.length > 0) {
    return withoutContactEntity(explicitPath.pathSegments, relationships);
  }

  const entity = getNonEmptyString(mapping.entity);
  if (!entity || entity === "global") {
    return [];
  }

  const resolvedEntity = resolveEntityNameFromNodeEntity(entity, relationships);
  if (!resolvedEntity || resolvedEntity === "global") {
    return [];
  }

  return getEntityContainmentPath(resolvedEntity, relationships, entityPathCache);
};

const buildPathWithIds = (entityPath: string[], levelIds: string[], field?: string) => {
  const segments: string[] = [];
  entityPath.forEach((segment, index) => {
    segments.push(segment, levelIds[index] || "1");
  });
  if (field) {
    segments.push(field);
  }
  return segments.join("/");
};

const resolveMultilineColumnField = (column: any, entityToken: string, entityMapping?: any) => {
  const columnEntity = getNonEmptyString(column?.entity);
  if (columnEntity) {
    const tokenEntity = getContactMultilineEntityValue(entityMapping, entityToken).entity;
    if (tokenEntity !== columnEntity) return undefined;
  }

  if (column?.multilineFields && typeof column.multilineFields === "object") {
    const mappedField = getNonEmptyString(column.multilineFields[entityToken]);
    if (mappedField) return mappedField;
  }
  return getNonEmptyString(column?.field);
};

const resolveEntityNameFromNodeEntity = (entityValue: string, relationships: any[]): string => {
  if (!entityValue || entityValue === "global") return "";
  if (relationships.some((rel) => rel?.name === entityValue)) {
    return entityValue;
  }

  const byTarget = relationships.find((rel) => rel?.target === entityValue && rel?.name);
  if (byTarget?.name) {
    return String(byTarget.name).trim();
  }

  return entityValue;
};

const inferColumnEntityFromRelease = (
  field: string,
  release: any,
  relationships: any[],
  cache: Map<string, string>,
) => {
  if (!field) return "";
  if (cache.has(field)) {
    return cache.get(field) || "";
  }

  let inferred = "";
  try {
    if (release?.rule_graph) {
      const graph = graphlib.json.read(release.rule_graph as any);
      const node = graph?.node?.(field) as any;
      const entityValue = typeof node?.entity === "string" ? node.entity.trim() : "";
      inferred = resolveEntityNameFromNodeEntity(entityValue, relationships);
    }
  } catch {
    inferred = "";
  }

  cache.set(field, inferred);
  return inferred;
};

const resolveColumnEntityPath = (
  column: any,
  field: string,
  release: any,
  relationships: any[],
  entityPathCache: Map<string, string[]>,
  columnEntityCache: Map<string, string>,
  row?: Record<string, any>,
): { entityPath: string[]; explicitIdsByPath: Map<string, string> } => {
  const explicitPathResult = row
    ? parseTemplatedEntityPathOverride(column?.path, row)
    : {
      ...parseEntityPathOverride(column?.path),
      explicitIdsByPath: new Map<string, string>(),
    };
  if (explicitPathResult.isExplicit) {
    return withoutContactEntityInExplicitPath(
      explicitPathResult.pathSegments,
      explicitPathResult.explicitIdsByPath,
      relationships,
    );
  }

  const entityFromColumn = typeof column?.entity === "string" ? column.entity.trim() : "";
  const entity = entityFromColumn || inferColumnEntityFromRelease(field, release, relationships, columnEntityCache);
  if (entity && entity !== "global") {
    return {
      entityPath: getEntityContainmentPath(entity, relationships, entityPathCache),
      explicitIdsByPath: new Map<string, string>(),
    };
  }

  return { entityPath: [], explicitIdsByPath: new Map<string, string>() };
};

const mapContactCsvRows = async (
  mappingDef: any,
  release: any,
  input: unknown,
  options: StructuredCsvMapOptions = CONTACT_CSV_MAP_OPTIONS,
): Promise<ContactRecord[]> => {
  const rows = await toCsvRows(input);
  const columns = Array.isArray(mappingDef?.columns) ? mappingDef.columns : [];
  const reservedSet = new Set<string>(options.reservedFields);
  const relationships = Array.isArray(release?.relationships) ? release.relationships : [];
  const entityPathCache = new Map<string, string[]>();
  const columnEntityCache = new Map<string, string>();
  const records: ContactRecord[] = [];
  const recordsById = new Map<string, ContactRecord>();

  rows.forEach((row) => {
    let mapped: ContactRecord;
    if (options.mergeRowsByReservedId) {
      let rowContactId: string | undefined;
      for (const column of columns) {
        const field = typeof column?.field === "string" ? column.field.trim() : "";
        if (field !== "id") continue;
        rowContactId = normalizeContactRecordId(getContactColumnValue(column, row, mappingDef));
        if (rowContactId) break;
      }

      if (rowContactId) {
        const existing = recordsById.get(rowContactId);
        if (existing) {
          mapped = existing;
        } else {
          mapped = { id: rowContactId };
          recordsById.set(rowContactId, mapped);
          records.push(mapped);
        }
      } else {
        mapped = {};
        records.push(mapped);
      }
    } else {
      mapped = {};
      records.push(mapped);
    }

    addMappingExternalIdentifier(mapped, mappingDef, row);

      const identifiersByPath = new Map<string, string>();

      columns.forEach((column: any) => {
        const field = typeof column?.field === "string" ? column.field.trim() : "";
        if (!field) return;

        const resolvedPath = resolveColumnEntityPath(
          column,
          field,
          release,
          relationships,
          entityPathCache,
          columnEntityCache,
          row,
        );

        resolvedPath.explicitIdsByPath.forEach((id, pathKey) => {
          identifiersByPath.set(pathKey, id);
        });
      });

      columns.forEach((column: any) => {
        if (!isIdentifierColumn(column)) return;
        const field = typeof column?.field === "string" ? column.field.trim() : "";
        if (!field) return;

        const value = getContactColumnValue(column, row, mappingDef);
        const normalizedIdentifier = normalizeIdentifierValue(value);

        const resolvedPath = resolveColumnEntityPath(
          column,
          field,
          release,
          relationships,
          entityPathCache,
          columnEntityCache,
          row,
        );
        const path = resolvedPath.entityPath;
        if (path.length === 0) return;

        const pathKey = normalizePathKey(path);
        if (!identifiersByPath.has(pathKey)) {
          identifiersByPath.set(pathKey, normalizedIdentifier);
        }
      });

      columns.forEach((column: any) => {
        const field = typeof column?.field === "string" ? column.field.trim() : "";
        const value = getContactColumnValue(column, row, mappingDef);
        if (value === undefined) return;
        addExternalIdentifier(mapped, column, value);
        if (!field) return;
        assertSafePathSegment(field, "field");

        if (reservedSet.has(field)) {
          if (field === "id") {
            const normalizedId = normalizeContactRecordId(value);
            if (normalizedId) {
              mapped[field] = normalizedId;
            }
            return;
          }
          mapped[field] = value;
          return;
        }

        const resolvedPath = resolveColumnEntityPath(
          column,
          field,
          release,
          relationships,
          entityPathCache,
          columnEntityCache,
          row,
        );
        const entityPath = resolvedPath.entityPath;
        if (entityPath.length > 0) {
          const setPath = getSetAtPathForField(entityPath, field, identifiersByPath);
          setAtPath(mapped, setPath, value);
          return;
        }

        mapped[field] = value;
      });
  });

  return records.filter((record) => Object.keys(record).length > 0);
};

const mapEntityCsvRows = async (
  mappingDef: any,
  input: unknown,
): Promise<ContactRecord[]> => {
  const template = getExtensionJsonTemplate(mappingDef?.data);
  if (!template || Object.keys(template).length === 0) {
    const rows = await toCsvRows(input);
    const columns = Array.isArray(mappingDef?.columns) ? mappingDef.columns : [];
    const records: ContactRecord[] = [];
    const recordsById = new Map<string, ContactRecord>();

    rows.forEach((row) => {
      const identifierColumns = columns.filter((column: any) => isIdentifierColumn(column));
      const rowId = identifierColumns
        .map((column: any) => normalizeContactRecordId(getEntityColumnValue(column, row, mappingDef)))
        .find((value: string | undefined): value is string => Boolean(value));

      let mapped: ContactRecord;
      if (rowId) {
        const existing = recordsById.get(rowId);
        if (existing) {
          mapped = existing;
        } else {
          mapped = { id: rowId };
          recordsById.set(rowId, mapped);
          records.push(mapped);
        }
      } else {
        mapped = {};
        records.push(mapped);
      }

      columns.forEach((column: any) => {
        const field = typeof column?.field === "string" ? column.field.trim() : "";
        if (!field) return;

        const value = getEntityColumnValue(column, row, mappingDef);
        if (value === undefined) return;
        addExternalIdentifier(mapped, column, value);

        if (isIdentifierColumn(column)) {
          const normalizedId = normalizeContactRecordId(value);
          if (normalizedId && !mapped.id) {
            mapped.id = normalizedId;
            if (!recordsById.has(normalizedId)) {
              recordsById.set(normalizedId, mapped);
            }
          }
        }

        setEntityFieldValueAtPath(mapped, field, value);
      });
    });

    return records.filter((record) => Object.keys(record).length > 0);
  }

  assertSafeExtensionTemplate(template);
  const rows = await toCsvRows(input);
  return rows
    .map((row) => renderExtensionTemplate(template, row))
    .filter(
      (record): record is ContactRecord =>
        record !== null &&
        record !== undefined &&
        typeof record === "object" &&
        !Array.isArray(record) &&
        Object.keys(record).length > 0,
    );
};

const mapContactCsvRowsMultiline = async (
  mappingDef: any,
  release: any,
  input: unknown,
  options: StructuredCsvMapOptions = CONTACT_CSV_MAP_OPTIONS,
): Promise<ContactRecord[]> => {
  const rows = await toCsvRows(input);
  const columns = Array.isArray(mappingDef?.columns) ? mappingDef.columns : [];
  const entityMapping = mappingDef?.entityMapping || {};
  const entityTypeColumn = getNonEmptyString(entityMapping.column);
  const identifierColumn = getNonEmptyString(entityMapping.identifierColumn);
  const reservedSet = new Set<string>(options.reservedFields);
  const relationships = Array.isArray(release?.relationships) ? release.relationships : [];
  const entityPathCache = new Map<string, string[]>();
  const getColumnValue = options.typeLabel === "entity" ? getEntityColumnValue : getContactColumnValue;
  const normalizeRowIdentifier = options.typeLabel === "entity"
    ? normalizeEntityIdentifierValue
    : normalizeIdentifierValue;

  if (!entityTypeColumn || !identifierColumn) {
    throw new Error(`${options.typeLabel} multiline CSV mapping requires entity and identifier columns`);
  }

  const records: ContactRecord[] = [];
  const recordsById = new Map<string, ContactRecord>();
  let currentContact: ContactRecord | undefined;
  let currentPathIds = new Map<string, string>();

  rows.forEach((row) => {
    const entityToken = getNonEmptyString(row[entityTypeColumn]);
    if (!entityToken) return;

    const entityPath = resolveMultilineEntityPath(entityToken, entityMapping, relationships, entityPathCache);
    const rowIdentifier = normalizeRowIdentifier(row[identifierColumn]);
    const isGlobal = entityPath.length === 0;

    if (isGlobal) {
      const contactId = normalizeContactRecordId(row[identifierColumn])?.replace(/\//g, "-");
      if (contactId) {
        const existing = recordsById.get(contactId);
        if (existing) {
          currentContact = existing;
        } else {
          currentContact = { id: contactId };
          recordsById.set(contactId, currentContact);
          records.push(currentContact);
        }
      } else {
        currentContact = {};
        records.push(currentContact);
      }
      currentPathIds = new Map<string, string>();
    } else if (!currentContact) {
      currentContact = {};
      records.push(currentContact);
      currentPathIds = new Map<string, string>();
    }

    const levelIds: string[] = [];
    if (!isGlobal) {
      entityPath.forEach((_, index) => {
        const pathKey = normalizePathKey(entityPath.slice(0, index + 1));
        const isLeaf = index === entityPath.length - 1;
        const id = isLeaf
          ? (rowIdentifier || currentPathIds.get(pathKey) || "1")
          : (currentPathIds.get(pathKey) || "1");
        levelIds.push(id);
        currentPathIds.set(pathKey, id);
      });
    }

    let mappedAnyField = false;
    columns.forEach((column: any) => {
      const header = getNonEmptyString(column?.header);
      if (!header) return;
      if (header === entityTypeColumn || header === identifierColumn) return;

      const field = resolveMultilineColumnField(column, entityToken, entityMapping);
      if (!field) return;
      assertSafePathSegment(field, "field");

      const value = getColumnValue(column, row, mappingDef);
      if (value === undefined) return;
      if (currentContact) {
        addExternalIdentifier(currentContact, column, value);
      }

      if (reservedSet.has(field)) {
        if (field === "id") {
          const normalizedId = normalizeContactRecordId(value);
          if (normalizedId && currentContact) {
            currentContact.id = normalizedId;
            if (!recordsById.has(normalizedId)) {
              recordsById.set(normalizedId, currentContact);
            }
          }
        } else if (currentContact) {
          currentContact[field] = value;
        }
        mappedAnyField = true;
        return;
      }

      if (isGlobal || entityPath.length === 0) {
        if (currentContact) {
          currentContact[field] = value;
          mappedAnyField = true;
        }
        return;
      }

      if (currentContact) {
        const setPath = buildPathWithIds(entityPath, levelIds, field);
        setAtPath(currentContact, setPath, value);
        mappedAnyField = true;
      }
    });

    if (!isGlobal && !mappedAnyField && currentContact) {
      const entityOnlyPath = buildPathWithIds(entityPath, levelIds);
      setAtPath(currentContact, entityOnlyPath, {});
    }
  });

  return records.filter((record) => Object.keys(record).length > 0);
};

export const normalizeMappedRecords = (mappedData: unknown): ContactRecord[] => {
  if (!mappedData) return [];
  if (Array.isArray(mappedData)) {
    return mappedData.filter((item) => item && typeof item === "object") as ContactRecord[];
  }
  if (typeof mappedData === "object") {
    return [mappedData as ContactRecord];
  }
  throw new Error("Mapped payload must be an object or array");
};

export const mapContactData = async (
  mappingDef: any,
  release: any,
  input: unknown,
): Promise<ContactRecord[]> => {
  if (!mappingDef) return [];
  const mode = typeof mappingDef.mode === "string" ? mappingDef.mode.toLowerCase() : "";

  if (mappingDef.type === "contact" && mode === "csv") {
    if (mappingDef?.entityMapping?.type === "multiline") {
      return mapContactCsvRowsMultiline(mappingDef, release, input, CONTACT_CSV_MAP_OPTIONS);
    }
    return mapContactCsvRows(mappingDef, release, input, CONTACT_CSV_MAP_OPTIONS);
  }

  const mappedData = await mapRequestData(mappingDef, release, input);
  return normalizeMappedRecords(mappedData);
};

export const mapEntityData = async (
  mappingDef: any,
  release: any,
  input: unknown,
): Promise<ContactRecord[]> => {
  if (!mappingDef) return [];
  const mode = typeof mappingDef.mode === "string" ? mappingDef.mode.toLowerCase() : "";

  if (mappingDef.type === "entities" && mode === "csv") {
    if (!mappingDef?.data && mappingDef?.entityMapping?.type === "multiline") {
      return mapContactCsvRowsMultiline(mappingDef, release, input, LEGACY_ENTITY_CSV_MAP_OPTIONS);
    }
    return mapEntityCsvRows(mappingDef, input);
  }

  const mappedData = await mapRequestData(mappingDef, release, input);
  return normalizeMappedRecords(mappedData);
};

export const buildContactCreatePayload = (input: ContactRecord): ContactRecord => {
  const root = CONTACT_ROOT_FIELDS.reduce((acc: ContactRecord, key: string) => {
    if (input[key] !== undefined) {
      acc[key] = input[key];
    }
    return acc;
  }, {});

  const generatedName = [input.firstName, input.lastName].filter(Boolean).join(" ").trim();
  const name = input.name || generatedName || undefined;
  const custom = omit(input, [...CONTACT_ROOT_FIELDS, "id", "name", CONTACT_EXTERNAL_IDENTIFIERS_FIELD]);

  return {
    ...root,
    ...(name ? { name } : {}),
    custom,
  };
};

const isContactCustomData = (value: unknown): value is ContactCustomData =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getEntityInstanceId = (value: unknown): string | undefined => {
  if (!isContactCustomData(value)) return undefined;
  const id = value["@id"];
  if (typeof id !== "string" && typeof id !== "number") return undefined;
  const normalized = String(id).trim();
  return normalized || undefined;
};

const cloneContactCustomValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map((item) => cloneContactCustomValue(item));
  if (isContactCustomData(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, cloneContactCustomValue(item)]),
    );
  }
  return value;
};

const isEntityInstanceArray = (value: unknown[]): boolean =>
  value.length > 0 && value.every((item) => getEntityInstanceId(item) !== undefined);

const mergeContactCustomValue = (existing: unknown, incoming: unknown): unknown => {
  // A { "@clear": true } marker replaces the stored value, for the write to remove the key.
  if (isClearMarker(incoming)) return incoming;
  if (Array.isArray(incoming)) {
    if (!Array.isArray(existing) || !isEntityInstanceArray(incoming)) {
      return cloneContactCustomValue(incoming);
    }
    if (existing.length > 0 && !isEntityInstanceArray(existing)) {
      return cloneContactCustomValue(incoming);
    }

    const merged = existing.map((item) => cloneContactCustomValue(item));
    const indexById = new Map<string, number>();
    for (const [index, item] of merged.entries()) {
      const id = getEntityInstanceId(item);
      if (id !== undefined) indexById.set(id, index);
    }

    for (const item of incoming) {
      const id = getEntityInstanceId(item);
      if (id === undefined) continue;
      const existingIndex = indexById.get(id);
      if (existingIndex === undefined) {
        indexById.set(id, merged.length);
        merged.push(cloneContactCustomValue(item));
        continue;
      }
      merged[existingIndex] = mergeContactCustomValue(merged[existingIndex], item);
    }
    return merged;
  }

  if (isContactCustomData(incoming)) {
    const existingData = isContactCustomData(existing) ? existing : {};
    const merged: ContactCustomData = Object.fromEntries(
      Object.entries(existingData).map(([key, value]) => [key, cloneContactCustomValue(value)]),
    );
    for (const [key, value] of Object.entries(incoming)) {
      if (UNSAFE_PATH_SEGMENTS.has(key)) continue;
      merged[key] = mergeContactCustomValue(existingData[key], value);
    }
    return merged;
  }

  return incoming;
};

/**
 * Merges mapped contact custom data while matching Decisively entity instances by `@id`.
 * Object fields are merged recursively, entity arrays retain unmatched existing instances,
 * and arrays without entity IDs keep replacement semantics.
 *
 * @param existing Current persisted contact custom data.
 * @param incoming Newly mapped contact custom data.
 * @returns A new merged object without mutating either input.
 */
export const mergeContactCustomDataByEntityId = (
  existing: unknown,
  incoming: unknown,
): ContactCustomData => {
  const merged = mergeContactCustomValue(existing, incoming);
  return isContactCustomData(merged) ? merged : {};
};

export const buildContactUpdatePayload = (input: ContactRecord): ContactRecord => {
  const root = [...CONTACT_ROOT_FIELDS, "name"].reduce((acc: ContactRecord, key: string) => {
    if (input[key] !== undefined) {
      acc[key] = input[key];
    }
    return acc;
  }, {});

  const custom = omit(input, [
    ...CONTACT_ROOT_FIELDS,
    "id",
    "lastModified",
    "created",
    "name",
    "custom",
    CONTACT_EXTERNAL_IDENTIFIERS_FIELD,
  ]);
  return {
    ...root,
    custom: {
      ...(input.custom && typeof input.custom === "object" ? input.custom : {}),
      ...custom,
    },
  };
};
